import { store, computed, action, observable } from '~/common/mobx.decorator'
import { apolloClient, GET_WORKER_DETAIL_QUERY, MANUAL_SYNC_PROVILDER } from '~/common/apollo'
import { authStore } from '~/stores/auth.store'
import { handleGraphQLError } from '~/common/helpers/graphql.helper'
import { PIPELINE_STAGE_TRACKING_KEY } from '~/common/constants'
import { masterStore } from './master.store'
import { checklistsStore } from '~/features/care-credential/components/checklists/checklists.store'

/**
 * Contains all information about the candidate / worker
 */
@store()
class CandidateStore {
	// Full candidate information
	@observable candidate = null
	@observable showNavigationPostHire = false

	@computed
	get agreedToTerms() {
		return this.candidate?.agreedToTerms || false
	}

	@computed
	get currentInProgressStep() {
		return this.currentPipelineStageTrackingSteps.find(
			(step) => step.status === 'in_progress' || (step.code === PIPELINE_STAGE_TRACKING_KEY.START_WORK && step.status === 'completed')
		)
	}

	@computed
	get currentPipelineStageMapped() {
		return this.candidate?.currentPipelineStageMapped
	}

	@computed get hasConfirmedStartWork() {
		return Boolean(this.candidate?.hasConfirmedStartWork)
	}

	@computed
	get currentPipelineStageTrackingSteps() {
		if (!this.candidate?.currentPipelineStageTracking) {
			return []
		}
		let firstPendingIndex = -1

		return Object.keys(this.candidate?.currentPipelineStageTracking).map((key, index) => {
			if (this.candidate?.currentPipelineStageTracking[key]?.progress_status === 'pending' && firstPendingIndex === -1) {
				firstPendingIndex = index
			}
			return {
				code: key,
				title: this.candidate?.currentPipelineStageTracking[key]?.name,
				status: firstPendingIndex > -1 && firstPendingIndex !== index ? 'not-start' : this.candidate?.currentPipelineStageTracking[key]?.progress_status,
			}
		})
	}

	@computed
	get isCandidatePostHireContent() {
		return (
			['RN NCLEX', 'MT'].includes((this?.candidate?.candidateType ?? '').toUpperCase()) &&
			['H1B', 'H1B+EB3'].includes((this?.candidate.visaType ?? '').toUpperCase())
		)
	}

	@computed
	get postHireContent() {
		if (this.isCandidatePostHireContent) {
			return masterStore.candidatePostHireContent
		}

		return masterStore.workerPostHireContent
	}

	@computed
	get hasUnaAccount() {
		return !!this.candidate?.sclProvider
	}

	@computed
	get shouldConfirmCreateUnaAccount() {
		return (this.candidate?.customField1 === 'New Lead' || this.candidate?.customField1 === 'Prospect') && !this.candidate?.sclProvider
	}

	@action
	fetchSlcProvider = async () => {
		await apolloClient
			.mutate({
				mutation: MANUAL_SYNC_PROVILDER,
			})
			.then(async () => {
				this.fetchWorkerBasicInfo()
				await checklistsStore.getUnaLink()
				await checklistsStore.refetchChecklists()
			})
	}

	@computed
	get hasOnlyOfferAccepted() {
		return this.candidate?.fromOfferAcceptedJobIds && this.candidate?.fromOfferAcceptedJobIds.length === 1
	}

	@computed
	get hasMultipleOfferAccepted() {
		return this.candidate?.fromOfferAcceptedJobIds && this.candidate?.fromOfferAcceptedJobIds.length > 1
	}

	@computed
	get offerAcceptedJobId() {
		return this.candidate?.fromOfferAcceptedJobIds?.[0]
	}

	@action
	updateCandidateInfo = (candidate) => {
		this.candidate = candidate
	}

	/**
	 * Function use to fetch candidate/worker detail based on `query`
	 *
	 * @param {*} options
	 */
	@action
	fetchCandidateDetail = async (options) => {
		try {
			const { query } = options || {}
			const { data } = await apolloClient.query({
				query: query || GET_WORKER_DETAIL_QUERY,
				variables: { id: authStore.id },
			})

			const { worker } = data
			authStore.setWorker(worker)

			// The worker has not completed the initial screening must complete the profile
			if (!worker?.initialScreeningResult) {
				authStore.setRequireCompleteProfile(true)
				await authStore.changeProfile(worker)
				return
			}

			this.candidate = worker
			await authStore.changeProfile(worker)
		} catch (e) {
			handleGraphQLError(e?.graphQLErrors)
		}
	}

	@action
	setShowNavigationPostHire = (value) => {
		this.showNavigationPostHire = value
	}
}

const candidateStore = new CandidateStore()
export default candidateStore
